import React, { useEffect, useState, useCallback } from "react";
import { Table, Select, Button, Space } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import { loggedInUsers } from "../../store/store";
import { allList, getContacts } from "../../libs/dashboard";
import { formatNumber } from "../../libs/helper";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

var CryptoJS = require("crypto-js");
var userGroup;
let userGroupNames;

const ViewList = () => {
  const { userdata } = loggedInUsers((state) => state.auth);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [selectedGroupName, setSelectedGroupName] = useState("");
  const [contacts, setContacts] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);

  // Decrypt
  var getUser = CryptoJS.AES.decrypt(
    userdata,
    process.env.REACT_APP_SOCKET_ENCRYPTION_KEY
  );
  var userD = JSON.parse(getUser.toString(CryptoJS.enc.Utf8));

  // Fetch all groups
  const fetchGroups = useCallback(async () => {
    setGroupsLoading(true);
    try {
      const getGrp = await allList({
        email: userD?.email,
        bearerToken: userD?.bearerToken,
      });
      userGroup = getGrp.data.data;
      userGroupNames = userGroup.map((group) => ({
        value: group.id,
        label: group.name,
        totalContacts: group.totalNumberOfContacts,
      }));
      setGroups(userGroupNames);
      
      // Check if groupId is in URL params
      const groupIdFromUrl = searchParams.get("groupId");
      if (groupIdFromUrl) {
        const group = userGroup.find((g) => g.id === groupIdFromUrl);
        if (group) {
          setSelectedGroupId(groupIdFromUrl);
          setSelectedGroupName(group.name);
        }
      }
    } catch (error) {
      console.error("Error fetching groups:", error);
      toast.error("Failed to load contact groups");
    } finally {
      setGroupsLoading(false);
    }
  }, [userD?.email, userD?.bearerToken, searchParams]);

  // Fetch contacts for selected group
  const fetchContacts = useCallback(async () => {
    if (!selectedGroupId) {
      setContacts([]);
      setTotalRows(0);
      return;
    }

    setLoading(true);
    try {
      const response = await getContacts({
        userId: userD?.email,
        groupId: selectedGroupId,
        bearerToken: userD?.bearerToken,
        page: currentPage,
        perPage: perPage,
      });

      if (response && response.data) {
        // API returns array directly: [{"phoneNumber": "...", "createdAt": "..."}]
        const contactsData = Array.isArray(response.data) 
          ? response.data 
          : response.data.data || [];
        setContacts(contactsData);
        setTotalRows(
          parseInt(response.headers["x-pagination-totalcount"] || contactsData.length, 10)
        );
      }
    } catch (error) {
      console.error("Error fetching contacts:", error);
      toast.error("Failed to load contacts");
      setContacts([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [selectedGroupId, currentPage, perPage, userD?.email, userD?.bearerToken]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  useEffect(() => {
    if (selectedGroupId) {
      fetchContacts();
    }
  }, [selectedGroupId, currentPage, perPage, fetchContacts]);

  const handleGroupChange = (groupId) => {
    setSelectedGroupId(groupId);
    const selectedGroup = groups.find((g) => g.value === groupId);
    setSelectedGroupName(selectedGroup ? selectedGroup.label : "");
    setCurrentPage(1); // Reset to first page when changing groups
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setPerPage(newPerPage);
    setCurrentPage(page);
  };

  const columns = [
    {
      title: "Phone Number",
      dataIndex: "phoneNumber",
      key: "phoneNumber",
      width: "50%",
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      width: "50%",
    },
  ];

  return (
    <div>
      <h1 className="text-lg font-semibold p-1">View Contact List</h1>
      
      <div className="p-2">
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <div style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
            <label style={{ minWidth: "120px" }}>Select Group:</label>
            <Select
              style={{ minWidth: "250px" }}
              placeholder="Select a contact group"
              value={selectedGroupId}
              onChange={handleGroupChange}
              options={groups}
              loading={groupsLoading}
              allowClear
              onClear={() => {
                setSelectedGroupId(null);
                setSelectedGroupName("");
                setContacts([]);
                setTotalRows(0);
              }}
            />
            <Button onClick={() => navigate("/dashboard/address-book")}>
              Back to Groups
            </Button>
          </div>

          {selectedGroupId && (
            <div>
              <p className="text-xs font-semibold mb-2">
                <span className="text-xl font-semibold">
                  {formatNumber(totalRows)}
                </span>{" "}
                Contacts in "{selectedGroupName}"
              </p>
            </div>
          )}

          <Table
            columns={columns}
            dataSource={contacts}
            rowKey={(record, index) => record.id || record.phoneNumber || `contact-${index}`}
            loading={loading}
            pagination={{
              current: currentPage,
              pageSize: perPage,
              total: totalRows,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              showTotal: (total, range) =>
                `Showing ${range[0]}-${range[1]} of ${total} contacts`,
            }}
            onChange={(pagination) => {
              handlePageChange(pagination.current);
              if (pagination.pageSize !== perPage) {
                handlePerRowsChange(pagination.pageSize, 1);
              }
            }}
            locale={{
              emptyText: selectedGroupId
                ? "No contacts found in this group"
                : "Please select a contact group to view contacts",
            }}
          />
        </Space>
      </div>
      <ToastContainer autoClose={3000} theme="colored" newestOnTop={true} />
    </div>
  );
};

export default ViewList;
